import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import api from '../../api/api.js';
import Button from '../common/Button.jsx';
import { FaEdit, FaTrashAlt, FaPlus, FaSpinner } from 'react-icons/fa';
import DynamicDateInput from '../common/DynamicDateInput.jsx';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetEventsQuery } from '../../redux/api/apiSlice';

const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText, isAlert = false }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center z-[1000] font-body">
            <div className="bg-card p-6 rounded-2xl shadow-soft w-full max-w-sm m-4 transform transition-all">
                <h3 className="text-xl font-bold font-heading text-primaryDark mb-4">{title}</h3>
                <p className="text-gray-700 mb-6">{message}</p>
                <div className="flex justify-end space-x-3">
                    {!isAlert && (
                        <Button onClick={onCancel} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium px-4 py-2 rounded-lg transition-colors">Cancel</Button>
                    )}
                    <Button onClick={onConfirm} className={`font-medium px-4 py-2 rounded-lg transition-colors ${isAlert ? 'bg-pink-500 hover:bg-pink-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}`}>{confirmText}</Button>
                </div>
            </div>
        </div>
    );
};

const EventTable = ({ events, handleEdit, handleDelete }) => (
    <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-background">
            <thead className="bg-background/50">
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium font-heading text-primaryDark uppercase tracking-wider">Event Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium font-heading text-primaryDark uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium font-heading text-primaryDark uppercase tracking-wider">Dates</th>
                    <th className="px-4 py-3 text-left text-xs font-medium font-heading text-primaryDark uppercase tracking-wider">Actions</th>
                </tr>
            </thead>
            <tbody className="bg-card divide-y divide-background">
                {events.map(event => (
                    <tr key={event._id} className="hover:bg-background transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{event.name}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{event.location}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {new Date(event.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' })} - {new Date(event.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm flex space-x-3">
                            <button onClick={() => handleEdit(event)} className="text-pink-500 hover:text-pink-700"><FaEdit /></button>
                            <button onClick={() => handleDelete(event._id)} className="text-red-500 hover:text-red-700"><FaTrashAlt /></button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const ManageEvents = () => {
    const navigate = useNavigate();
    const { data: rawEvents, isLoading: loading, isError, refetch } = useGetEventsQuery();
    const events = useMemo(() => Array.isArray(rawEvents) ? rawEvents : [], [rawEvents]);

    const [newEvent, setNewEvent] = useState({ name: '', description: '', location: '', startDate: '', endDate: '', bookingStartDate: '', bookingEndDate: '', isBookingOpen: true, bookingClosedMessage: 'Bookings closed' });
    const [error, setError] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState({ title: '', message: '', onConfirm: () => { }, onCancel: () => { }, confirmText: '', isAlert: false });
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (isError) setError('Failed to fetch events.');
    }, [isError]);

    const fetchEvents = () => {
        refetch();
    };

    const filteredEventsList = useMemo(() => {
        if (!searchTerm.trim()) return events;
        const lowSearch = searchTerm.toLowerCase();
        return events.filter(ev =>
            ev.name.toLowerCase().includes(lowSearch) ||
            (ev.location && ev.location.toLowerCase().includes(lowSearch))
        );
    }, [events, searchTerm]);

    const validateDates = (eventData) => {
        const { startDate, endDate, bookingStartDate, bookingEndDate } = eventData;
        const s = new Date(startDate);
        const e = new Date(endDate);
        const bs = new Date(bookingStartDate);
        const be = new Date(bookingEndDate);
        if (bs > be) return 'Booking start date cannot be after booking end date.';
        if (s > e) return 'Event start date cannot be after event end date.';
        if (bs > s) return 'Booking should start before the event starts.';
        if (be > e) return 'Booking should end before or when the event ends.';
        return null;
    };

    const handleAddEvent = async (e) => {
        e.preventDefault();
        const validationError = validateDates(newEvent);
        if (validationError) {
            setError(validationError);
            toast.error(validationError);
            return;
        }
        try {
            await api.post('/events', newEvent);
            setNewEvent({ name: '', description: '', location: '', startDate: '', endDate: '', bookingStartDate: '', bookingEndDate: '', isBookingOpen: true, bookingClosedMessage: 'Bookings closed' });
            fetchEvents();
            navigate('/admin/manage-buildings');
        } catch {
            setError('Failed to add event.');
            toast.error('Failed to add event.');
        }
    };

    const handleUpdateEvent = async (e) => {
        e.preventDefault();
        const validationError = validateDates(editingEvent);
        if (validationError) {
            setError(validationError);
            toast.error(validationError);
            return;
        }
        try {
            await api.put(`/events/${editingEvent._id}`, editingEvent);
            setEditingEvent(null);
            fetchEvents();
        } catch {
            setError('Failed to update event.');
            toast.error('Failed to update event.');
        }
    };

    const confirmDelete = async (id) => {
        setIsModalOpen(false);
        try {
            await api.delete(`/events/${id}`);
            fetchEvents();
        } catch (err) {
            if (err.response?.status === 409) {
                setModalData({
                    title: 'Deletion Conflict',
                    message: 'A booking or building is currently assigned to this event. Remove all dependencies before deleting.',
                    confirmText: 'Got It',
                    isAlert: true,
                    onConfirm: () => setIsModalOpen(false),
                    onCancel: () => setIsModalOpen(false),
                });
                setIsModalOpen(true);
            } else {
                setError('Failed to delete event.');
            }
        }
    };

    const handleDeleteEvent = async (id) => {
        const eventName = events.find(e => e._id === id)?.name || 'this event';
        let message = `Are you sure you want to permanently delete the event "${eventName}"?`;

        try {
            const res = await api.get(`/events/${id}/bookings/exists`);
            if (res.data.exists) {
                message = `There are bookings for this event. If you delete this event, all associated bookings will also be deleted! Are you sure you want to permanently delete the event "${eventName}"?`;
            }
        } catch (error) {
            // If the check fails, proceed with the default message but log the error
            console.error("Could not check for bookings, proceeding with default delete confirmation.", error);
        }

        setModalData({
            title: 'Confirm Deletion',
            message,
            confirmText: 'Delete',
            isAlert: false,
            onConfirm: () => confirmDelete(id),
            onCancel: () => setIsModalOpen(false),
        });
        setIsModalOpen(true);
    };

    const { finishedEvents, ongoingEvents, upcomingEvents } = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize today to start of day for consistent comparison

        const finished = [];
        const ongoing = [];
        const upcoming = [];

        filteredEventsList.forEach(event => {
            const startDate = new Date(event.startDate);
            const endDate = new Date(event.endDate);
            endDate.setHours(23, 59, 59, 999); // Normalize end date to end of day

            if (endDate < today) {
                finished.push(event);
            } else if (startDate <= today && endDate >= today) {
                ongoing.push(event);
            } else {
                upcoming.push(event);
            }
        });
        return { finishedEvents: finished, ongoingEvents: ongoing, upcomingEvents: upcoming };
    }, [filteredEventsList]);

    if (loading) return <div className="text-center mt-10 text-xl text-primary font-body"><FaSpinner className="animate-spin inline mr-2" /> Loading Events...</div>;


    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 md:p-8 bg-neutral min-h-screen font-body">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <h2 className="text-3xl md:text-4xl font-bold font-heading text-primaryDark border-b-2 border-primary pb-2 flex-grow">Manage Events</h2>
                <div className="relative w-full md:w-64">
                    <input
                        type="text"
                        placeholder="Search events..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                </div>
            </div>
            {error && <div className="bg-red-100 text-red-700 p-3 rounded-xl mb-6 text-center shadow-md">{error}</div>}

            <div className="bg-card p-6 rounded-2xl shadow-soft mb-8">
                <h3 className="text-xl font-semibold font-heading text-primaryDark mb-4">Add New Event</h3>
                <form onSubmit={handleAddEvent} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input type="text" placeholder="Event Name" value={newEvent.name} onChange={e => setNewEvent({ ...newEvent, name: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-pink-300 focus:border-pink-500" required />
                    <input type="text" placeholder="Location" value={newEvent.location} onChange={e => setNewEvent({ ...newEvent, location: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-pink-300 focus:border-pink-500" required />
                    <textarea placeholder="Description" value={newEvent.description} onChange={e => setNewEvent({ ...newEvent, description: e.target.value })} className="sm:col-span-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-pink-300 focus:border-pink-500" required />
                    <div className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <DynamicDateInput
                            label="Start Date"
                            name="startDate"
                            value={newEvent.startDate}
                            onChange={e => setNewEvent({ ...newEvent, startDate: e.target.value })}
                            required
                        />
                        <DynamicDateInput
                            label="End Date"
                            name="endDate"
                            value={newEvent.endDate}
                            onChange={e => setNewEvent({ ...newEvent, endDate: e.target.value })}
                            required
                        />
                        <DynamicDateInput
                            label="Booking Start Date"
                            name="bookingStartDate"
                            value={newEvent.bookingStartDate}
                            onChange={e => setNewEvent({ ...newEvent, bookingStartDate: e.target.value })}
                            required
                        />
                        <DynamicDateInput
                            label="Booking End Date"
                            name="bookingEndDate"
                            value={newEvent.bookingEndDate}
                            onChange={e => setNewEvent({ ...newEvent, bookingEndDate: e.target.value })}
                            required
                        />
                    </div>
                    <div className="col-span-1 sm:col-span-2 space-y-2 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={newEvent.isBookingOpen}
                                onChange={e => setNewEvent({ ...newEvent, isBookingOpen: e.target.checked })}
                                className="form-checkbox h-5 w-5 text-pink-500 rounded focus:ring-pink-500"
                            />
                            <span className="text-gray-700 font-medium">Bookings Open</span>
                        </label>
                        {!newEvent.isBookingOpen && (
                            <input
                                type="text"
                                placeholder="Message to display when booking is closed (e.g., 'Bookings Full')"
                                value={newEvent.bookingClosedMessage}
                                onChange={e => setNewEvent({ ...newEvent, bookingClosedMessage: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-pink-300 focus:border-pink-500 text-sm"
                            />
                        )}
                    </div>
                    <div className="sm:col-span-2 pt-2">
                        <Button type="submit" className="w-full bg-pink-500 hover:bg-pink-600 text-white font-semibold py-2 rounded-lg shadow-md transition-colors">
                            <FaPlus className="inline mr-2" /> Add Event
                        </Button>
                    </div>
                </form>
            </div>

            {finishedEvents.length > 0 && (
                <div className="bg-card shadow-soft rounded-2xl overflow-x-auto mb-6">
                    <h3 className="text-xl font-semibold font-heading p-4 text-primaryDark border-b border-background">Finished Events</h3>
                    <EventTable events={finishedEvents} handleEdit={setEditingEvent} handleDelete={handleDeleteEvent} />
                </div>
            )}
            {ongoingEvents.length > 0 && (
                <div className="bg-card shadow-soft rounded-2xl overflow-x-auto mb-6">
                    <h3 className="text-xl font-semibold font-heading p-4 text-primaryDark border-b border-background">Ongoing Events</h3>
                    <EventTable events={ongoingEvents} handleEdit={setEditingEvent} handleDelete={handleDeleteEvent} />
                </div>
            )}
            {upcomingEvents.length > 0 && (
                <div className="bg-card shadow-soft rounded-2xl overflow-x-auto mb-6">
                    <h3 className="text-xl font-semibold font-heading p-4 text-primaryDark border-b border-background">Upcoming Events</h3>
                    <EventTable events={upcomingEvents} handleEdit={setEditingEvent} handleDelete={handleDeleteEvent} />
                </div>
            )}

            {editingEvent && (
                <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center z-[1000]">
                    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative p-6 bg-card w-full max-w-4xl rounded-2xl shadow-soft m-4">
                        <h3 className="text-2xl font-bold font-heading text-primaryDark mb-4">Edit Event: {editingEvent.name}</h3>
                        <form onSubmit={handleUpdateEvent} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <input type="text" placeholder="Event Name" value={editingEvent.name} onChange={e => setEditingEvent({ ...editingEvent, name: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg" required />
                            <input type="text" placeholder="Location" value={editingEvent.location} onChange={e => setEditingEvent({ ...editingEvent, location: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg" required />
                            <textarea placeholder="Description" value={editingEvent.description} onChange={e => setEditingEvent({ ...editingEvent, description: e.target.value })} className="sm:col-span-2 px-4 py-2 border border-gray-300 rounded-lg" required />
                            <div className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <DynamicDateInput
                                    label="Start Date"
                                    name="startDate"
                                    value={editingEvent.startDate?.split('T')[0]}
                                    onChange={e => setEditingEvent({ ...editingEvent, startDate: e.target.value })}
                                    required
                                />
                                <DynamicDateInput
                                    label="End Date"
                                    name="endDate"
                                    value={editingEvent.endDate?.split('T')[0]}
                                    onChange={e => setEditingEvent({ ...editingEvent, endDate: e.target.value })}
                                    required
                                />
                                <DynamicDateInput
                                    label="Booking Start Date"
                                    name="bookingStartDate"
                                    value={editingEvent.bookingStartDate?.split('T')[0]}
                                    onChange={e => setEditingEvent({ ...editingEvent, bookingStartDate: e.target.value })}
                                    required
                                />
                                <DynamicDateInput
                                    label="Booking End Date"
                                    name="bookingEndDate"
                                    value={editingEvent.bookingEndDate?.split('T')[0]}
                                    onChange={e => setEditingEvent({ ...editingEvent, bookingEndDate: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-span-1 sm:col-span-2 space-y-2 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={editingEvent.isBookingOpen !== undefined ? editingEvent.isBookingOpen : true}
                                        onChange={e => setEditingEvent({
                                            ...editingEvent,
                                            isBookingOpen: e.target.checked,
                                            bookingClosedMessage: !e.target.checked && !editingEvent.bookingClosedMessage ? 'Bookings closed' : editingEvent.bookingClosedMessage
                                        })}
                                        className="form-checkbox h-5 w-5 text-pink-500 rounded focus:ring-pink-500"
                                    />
                                    <span className="text-gray-700 font-medium">Bookings Open</span>
                                </label>
                                {!editingEvent.isBookingOpen && (
                                    <input
                                        type="text"
                                        placeholder="Message to display when booking is closed"
                                        value={editingEvent.bookingClosedMessage || ''}
                                        onChange={e => setEditingEvent({ ...editingEvent, bookingClosedMessage: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-pink-300 focus:border-pink-500 text-sm"
                                    />
                                )}
                            </div>
                            <div className="sm:col-span-2 flex justify-end space-x-3 pt-2">
                                <Button type="submit" className="bg-pink-500 hover:bg-pink-600 text-white font-medium">Update Event</Button>
                                <Button type="button" onClick={() => setEditingEvent(null)} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium">Cancel</Button>
                            </div>
                        </form>
                        <button onClick={() => setEditingEvent(null)} className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 text-xl">&times;</button>
                    </motion.div>
                </div>
            )}

            <ConfirmationModal
                isOpen={isModalOpen}
                title={modalData.title}
                message={modalData.message}
                onConfirm={modalData.onConfirm}
                onCancel={modalData.onCancel}
                confirmText={modalData.confirmText}
                isAlert={modalData.isAlert}
            />
        </motion.div>
    );
};

export default ManageEvents;
